<template>
  <q-page class="form-page">
    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-inner">
        <q-btn
          flat
          round
          dense
          icon="arrow_back"
          class="back-btn"
          to="/imoveis"
        />
        <div class="q-ml-md">
          <div class="page-title">Publicar Imóvel</div>
          <div class="page-subtitle">
            Preencha os dados do imóvel para publicação
          </div>
        </div>
      </div>
    </div>

    <!-- Form card -->
    <div class="form-wrapper">
      <q-card flat class="form-card">
        <!-- ── Section 1: Informações Básicas ── -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon-wrap">
              <q-icon name="info" size="18px" color="white" />
            </div>
            <div>
              <div class="section-title">Informações Básicas</div>
              <div class="section-sub">Identifique o seu imóvel</div>
            </div>
          </div>

          <div class="section-body">
            <q-input
              v-model="form.title"
              label="Título do imóvel"
              placeholder="Ex: Vivenda T4 com piscina em Sommerschield"
              outlined
              class="form-field"
              lazy-rules
              :rules="[
                (val) => (val && val.length > 0) || 'Título é obrigatório',
              ]"
            >
              <template v-slot:prepend>
                <q-icon name="title" color="grey-5" />
              </template>
            </q-input>

            <q-input
              v-model="form.description"
              label="Descrição"
              placeholder="Descreva os detalhes mais atractivos do imóvel..."
              type="textarea"
              rows="4"
              outlined
              class="form-field"
              lazy-rules
              :rules="[
                (val) => (val && val.length > 0) || 'Descrição é obrigatória',
              ]"
            >
              <template v-slot:prepend>
                <q-icon name="notes" color="grey-5" />
              </template>
            </q-input>

            <div class="row q-col-gutter-md">
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="form.property_type_id"
                  :options="propertyTypes"
                  label="Tipo de Imóvel"
                  option-value="id"
                  option-label="name"
                  emit-value
                  map-options
                  outlined
                  class="form-field"
                  lazy-rules
                  :rules="[(val) => val !== null || 'Selecione o tipo']"
                >
                  <template v-slot:prepend>
                    <q-icon name="home_work" color="grey-5" />
                  </template>
                </q-select>
              </div>
              <div class="col-12 col-sm-6">
                <q-input
                  v-model="form.location"
                  label="Localização"
                  placeholder="Ex: Maputo, Sommerschield"
                  outlined
                  class="form-field"
                  lazy-rules
                  :rules="[
                    (val) =>
                      (val && val.length > 0) || 'Localização é obrigatória',
                  ]"
                >
                  <template v-slot:prepend>
                    <q-icon name="location_on" color="grey-5" />
                  </template>
                </q-input>
              </div>
            </div>
          </div>
        </div>

        <div class="form-divider" />

        <!-- ── Section 2: Preço & Características ── -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon-wrap">
              <q-icon name="tune" size="18px" color="white" />
            </div>
            <div>
              <div class="section-title">Preço & Características</div>
              <div class="section-sub">Defina os atributos do imóvel</div>
            </div>
          </div>

          <div class="section-body">
            <!-- Price + Currency -->
            <div class="row q-col-gutter-md">
              <div class="col-8">
                <q-input
                  v-model.number="form.price"
                  label="Preço"
                  type="number"
                  min="0"
                  outlined
                  class="form-field"
                  lazy-rules
                  :rules="[(val) => val >= 0 || 'Preço inválido']"
                >
                  <template v-slot:prepend>
                    <q-icon name="payments" color="grey-5" />
                  </template>
                </q-input>
              </div>
              <div class="col-4">
                <q-select
                  v-model="form.currency"
                  :options="currencies"
                  label="Moeda"
                  outlined
                  class="form-field"
                  lazy-rules
                  :rules="[(val) => !!val || 'Obrigatório']"
                />
              </div>
            </div>

            <!-- Area -->
            <q-input
              v-model.number="form.area"
              label="Área"
              type="number"
              min="0"
              suffix="m²"
              outlined
              class="form-field"
              lazy-rules
              :rules="[(val) => val >= 0 || 'Área inválida']"
            >
              <template v-slot:prepend>
                <q-icon name="square_foot" color="grey-5" />
              </template>
            </q-input>

            <!-- Bedrooms / Bathrooms / Garage -->
            <div class="row q-col-gutter-md">
              <div class="col-4">
                <q-input
                  v-model.number="form.bedroom_amount"
                  label="Quartos"
                  type="number"
                  min="0"
                  outlined
                  class="form-field"
                  lazy-rules
                  :rules="[(val) => val >= 0 || 'Inválido']"
                >
                  <template v-slot:prepend>
                    <q-icon name="bed" color="grey-5" />
                  </template>
                </q-input>
              </div>
              <div class="col-4">
                <q-input
                  v-model.number="form.bathroom_amount"
                  label="Banheiros"
                  type="number"
                  min="0"
                  outlined
                  class="form-field"
                  lazy-rules
                  :rules="[(val) => val >= 0 || 'Inválido']"
                >
                  <template v-slot:prepend>
                    <q-icon name="bathtub" color="grey-5" />
                  </template>
                </q-input>
              </div>
              <div class="col-4">
                <q-input
                  v-model.number="form.garage_space"
                  label="Garagem"
                  type="number"
                  min="0"
                  outlined
                  class="form-field"
                  lazy-rules
                  :rules="[(val) => val >= 0 || 'Inválido']"
                >
                  <template v-slot:prepend>
                    <q-icon name="garage" color="grey-5" />
                  </template>
                </q-input>
              </div>
            </div>

            <!-- Condition + Objective -->
            <div class="row q-col-gutter-md">
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="form.condition_id"
                  :options="conditions"
                  label="Condição"
                  option-value="id"
                  option-label="name"
                  emit-value
                  map-options
                  outlined
                  class="form-field"
                  lazy-rules
                  :rules="[(val) => val !== null || 'Selecione a condição']"
                >
                  <template v-slot:prepend>
                    <q-icon name="star_rate" color="grey-5" />
                  </template>
                </q-select>
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="form.objective_id"
                  :options="objectives"
                  label="Objetivo"
                  option-value="id"
                  option-label="name"
                  emit-value
                  map-options
                  outlined
                  class="form-field"
                  lazy-rules
                  :rules="[(val) => val !== null || 'Selecione o objetivo']"
                >
                  <template v-slot:prepend>
                    <q-icon name="sell" color="grey-5" />
                  </template>
                </q-select>
              </div>
            </div>
          </div>
        </div>

        <div class="form-divider" />

        <!-- ── Section 3: Imagens ── -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon-wrap">
              <q-icon name="photo_library" size="18px" color="white" />
            </div>
            <div>
              <div class="section-title">Imagens</div>
              <div class="section-sub">
                {{ form.images.length }}/10 imagens adicionadas · Proporção 4:3, recomendado 1200 × 900 px (máximo 8000 × 8000 px)
              </div>
            </div>
          </div>

          <div class="section-body">
            <!-- Upload zone -->
            <div
              class="upload-zone"
              :class="{ 'upload-zone--disabled': form.images.length >= 10 }"
            >
              <q-file
                v-model="tempImage"
                multiple
                append
                accept="image/jpeg,image/png,image/jpg,image/webp"
                max-file-size="5242880"
                :max-files="10"
                :disable="form.images.length >= 10"
                @update:model-value="onImageSelected"
                @rejected="onRejected"
                class="upload-input"
                borderless
              >
                <template v-slot:default>
                  <div class="upload-prompt">
                    <q-icon
                      name="add_photo_alternate"
                      size="40px"
                      class="upload-icon"
                    />
                    <div class="upload-text">
                      {{
                        form.images.length >= 10
                          ? "Limite de 10 imagens atingido"
                          : "Clique para adicionar imagem"
                      }}
                    </div>
                    <div class="upload-hint">
                      JPEG, PNG, WEBP — máx. 5 MB por imagem · proporção 4:3
                    </div>
                  </div>
                </template>
              </q-file>
            </div>

            <div
              v-if="showImageError && form.images.length === 0"
              class="text-negative text-caption q-mt-sm"
            >
              Adicione pelo menos 1 imagem
            </div>

            <!-- Previews -->
            <div v-if="imagesPreviews.length > 0" class="q-mt-lg">
              <div class="row q-col-gutter-md">
                <div
                  v-for="(item, index) in imagesPreviews"
                  :key="index"
                  class="col-6 col-sm-4 col-md-3"
                >
                  <div class="preview-card">
                    <q-img :src="item.url" ratio="1" class="preview-img" />
                    <div class="preview-overlay">
                      <q-btn
                        round
                        dense
                        icon="delete"
                        color="negative"
                        size="sm"
                        @click="removeImage(index)"
                      />
                    </div>
                    <div class="preview-label">{{ item.name }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Actions ── -->
        <div class="form-actions">
          <q-btn
            flat
            label="Cancelar"
            to="/imoveis"
            class="action-btn action-btn--cancel"
          />
          <q-btn
            flat
            label="Limpar"
            @click="onReset"
            class="action-btn action-btn--reset"
          />
          <q-btn
            label="Publicar Imóvel"
            unelevated
            :loading="loading"
            class="action-btn action-btn--submit"
            @click="onSubmit"
          >
            <template v-slot:loading>
              <q-spinner-dots />
            </template>
          </q-btn>
        </div>
      </q-card>
    </div>

    <q-inner-loading
      :showing="loader"
      label="Por favor, aguarde..."
      label-class="text-primary"
      label-style="font-size: 1em"
    />
  </q-page>
</template>

<script>
// eslint-disable no-unused-vars
import store from "@/vuex/store";
import { ref, onBeforeUnmount } from "vue";
import Swal from "sweetalert2";
import router from "@/router";

export default {
  name: "PropertyForm",

  setup() {
    const loading = ref(false);
    const loader = ref(false);
    const tempImage = ref([]);
    const showImageError = ref(false);

    const form = ref({
      title: "",
      description: "",
      property_type_id: null,
      location: "",
      price: 0,
      currency: "MZN",
      area: 0,
      bedroom_amount: 0,
      bathroom_amount: 0,
      garage_space: 0,
      condition_id: null,
      objective_id: null,
      images: [],
    });

    const propertyTypes = ref([]);
    const conditions = ref([]);
    const objectives = ref([]);
    const currencies = ref(["MZN", "USD", "EUR", "ZAR"]);

    const imagesPreviews = ref([]);

    onBeforeUnmount(() => {
      imagesPreviews.value.forEach((preview) => {
        URL.revokeObjectURL(preview.url);
      });
    });

    getStoreOptions();

    function getStoreOptions() {
      loader.value = true;
      store.dispatch("imovel/getImovelOptions").then(() => {
        propertyTypes.value = store.state.imovel.submitOptions.property_types;
        conditions.value = store.state.imovel.submitOptions.conditions;
        objectives.value = store.state.imovel.submitOptions.objectives;
        loader.value = false;
      });
    }

    // const onImageSelected = (file) => {
    //   if (file) {
    //     if (form.value.images.length < 10) {
    //       let correctName = file.name;

    //       if (file.__key) {
    //         const keyString = file.__key.toString();
    //         const match = keyString.match(
    //           /\d+(.+\.(jpg|jpeg|png|gif|webp))\d+$/i,
    //         );
    //         if (match) {
    //           correctName = match[1];
    //         }
    //       }

    //       const correctedFile = new File([file], correctName, {
    //         type: file.type,
    //         lastModified: file.lastModified,
    //       });

    //       form.value.images.push(correctedFile);

    //       const preview = {
    //         url: URL.createObjectURL(correctedFile),
    //         name: `Imagem ${form.value.images.length}`,
    //         file: correctedFile,
    //       };
    //       imagesPreviews.value.push(preview);

    //       showImageError.value = false;
    //     }
    //     tempImage.value = null;
    //   }
    // };

    const onImageSelected = (files) => {
      if (!files) return;

      const filesArray = Array.isArray(files) ? files : [files];

      for (const file of filesArray) {
        if (form.value.images.length >= 10) break;

        let correctName = file.name;

        if (file.__key) {
          const keyString = file.__key.toString();
          const match = keyString.match(
            /\d+(.+\.(jpg|jpeg|png|gif|webp))\d+$/i,
          );
          if (match) {
            correctName = match[1];
          }
        }

        const correctedFile = new File([file], correctName, {
          type: file.type,
          lastModified: file.lastModified,
        });

        form.value.images.push(correctedFile);

        const preview = {
          url: URL.createObjectURL(correctedFile),
          name: `Imagem ${form.value.images.length}`,
          file: correctedFile,
        };
        imagesPreviews.value.push(preview);

        showImageError.value = false;
      }

      tempImage.value = [];
    };

    const removeImage = (index) => {
      URL.revokeObjectURL(imagesPreviews.value[index].url);
      form.value.images.splice(index, 1);
      imagesPreviews.value.splice(index, 1);
      imagesPreviews.value.forEach((preview, idx) => {
        preview.name = `Imagem ${idx + 1}`;
      });
    };

    const onRejected = (rejectedEntries) => {
      rejectedEntries.forEach((entry) => {
        if (entry.failedPropValidation === "max-file-size") {
          console.log(`${entry.file.name} excede 5MB`);
        } else if (entry.failedPropValidation === "accept") {
          console.log(`${entry.file.name} tem formato inválido`);
        }
      });
    };

    const fieldLabels = {
      title: "Título",
      description: "Descrição",
      property_type_id: "Tipo de imóvel",
      location: "Localização",
      price: "Preço",
      currency: "Moeda",
      area: "Área",
      bedroom_amount: "Quartos",
      bathroom_amount: "Banheiros",
      garage_space: "Garagem",
      condition_id: "Condição",
      objective_id: "Objetivo",
      images: "Imagens",
    };

    const translateValidationMessage = (msg) => {
      if (typeof msg !== "string") return "Valor inválido";

      const rules = [
        { re: /field is required/i, out: "é obrigatório" },
        { re: /selected .* is invalid/i, out: "seleção inválida" },
        { re: /must be a string/i, out: "deve ser texto" },
        { re: /must be an integer/i, out: "deve ser um número inteiro" },
        { re: /must be a number/i, out: "deve ser um número" },
        { re: /must be an array/i, out: "formato inválido" },
        {
          re: /must not be greater than (\d+) kilobytes?/i,
          out: (_, n) => `não deve exceder ${n} KB (${Math.round(n / 1024)} MB)`,
        },
        {
          re: /must be at least (\d+)/i,
          out: (_, n) => `deve ter pelo menos ${n}`,
        },
        {
          re: /must not be greater than (\d+)/i,
          out: (_, n) => `não deve ser maior que ${n}`,
        },
        { re: /must be an image/i, out: "deve ser uma imagem válida" },
        {
          re: /must be a file of type:?\s*(.+)/i,
          out: (_, types) => `deve ser um ficheiro do tipo: ${types.trim()}`,
        },
        { re: /has invalid image dimensions/i, out: "tem dimensões inválidas" },
        {
          re: /must have at least (\d+) items?/i,
          out: (_, n) => `deve ter pelo menos ${n} item(ns)`,
        },
        { re: /already been taken/i, out: "já está em uso" },
        { re: /must be a valid url/i, out: "deve ser um URL válido" },
      ];

      for (const { re, out } of rules) {
        if (re.test(msg)) {
          return typeof out === "function" ? msg.replace(re, out) : out;
        }
      }
      return msg;
    };

    const escapeHtml = (str) =>
      String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const isFieldMap = (v) =>
      v && typeof v === "object" && !Array.isArray(v);

    const handleSubmitError = (error) => {
      if (!error.response) {
        Swal.fire({
          icon: "error",
          title: "Erro de ligação",
          text: "Não foi possível ligar ao servidor. Verifique a sua conexão à Internet.",
        });
        return;
      }

      const status = error.response.status;
      const data = error.response.data || {};

      // Backend sends "errors" (plural) on 422; accept "error" too as fallback.
      const validationErrors = isFieldMap(data.errors)
        ? data.errors
        : isFieldMap(data.error)
          ? data.error
          : null;

      if (status === 422 && validationErrors) {
        const items = Object.entries(validationErrors)
          .flatMap(([field, msgs]) => {
            const rootKey = field.split(".")[0];
            const label = fieldLabels[rootKey] || rootKey;
            const list = Array.isArray(msgs) ? msgs : [msgs];
            return list.map(
              (m) =>
                `<li><strong>${escapeHtml(label)}:</strong> ${escapeHtml(
                  translateValidationMessage(m),
                )}</li>`,
            );
          })
          .join("");

        Swal.fire({
          icon: "warning",
          title: "Verifique os dados do formulário",
          html: `<ul style="text-align:left; padding-left:20px; margin:0; line-height:1.6;">${items}</ul>`,
          confirmButtonText: "OK",
        });
        return;
      }

      if (status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sessão expirada",
          text: "Inicie sessão novamente para continuar.",
        });
        return;
      }

      if (status === 403) {
        Swal.fire({
          icon: "error",
          title: "Sem permissão",
          text: "Não tem permissão para publicar imóveis.",
        });
        return;
      }

      if (status === 413) {
        Swal.fire({
          icon: "error",
          title: "Arquivos muito grandes",
          text: "As imagens excedem o tamanho máximo permitido. Reduza os ficheiros e tente novamente.",
        });
        return;
      }

      if (status >= 500) {
        // Backend 500 shape: { success:false, message:"Erro ao criar imóvel", error:"<exception>" }
        const backendMessage = data.message;
        const detail = typeof data.error === "string" ? data.error : null;

        Swal.fire({
          icon: "error",
          title: backendMessage || "Erro no servidor",
          text:
            detail ||
            "Ocorreu um problema no nosso servidor. Tente novamente mais tarde.",
        });
        return;
      }

      const fallback =
        Object.values(data.errors || {}).flat().join(" ") ||
        (typeof data.error === "string" && data.error) ||
        data.message ||
        "Erro ao publicar imóvel. Tente novamente.";

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: fallback,
      });
    };

    const onSubmit = async () => {
      if (form.value.images.length === 0) {
        showImageError.value = true;
        return;
      }

      loading.value = true;
      loader.value = true;

      try {
        const formData = new FormData();
        Object.keys(form.value).forEach((key) => {
          if (key !== "images") {
            formData.append(key, form.value[key]);
          }
        });

        form.value.images.forEach((image) => {
          formData.append("images[]", image);
        });

        await store.dispatch("imovel/storeImovel", formData);
        await Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Imovél publicado com sucesso!",
          showConfirmButton: false,
          timer: 1500,
        });
        onReset();
        router.push("/imoveis");
      } catch (error) {
        handleSubmitError(error);
      } finally {
        loading.value = false;
        loader.value = false;
      }
    };

    const onReset = () => {
      imagesPreviews.value.forEach((preview) => {
        URL.revokeObjectURL(preview.url);
      });

      form.value = {
        title: "",
        description: "",
        property_type_id: null,
        location: "",
        price: 0,
        currency: "MZN",
        area: 0,
        bedroom_amount: 0,
        bathroom_amount: 0,
        garage_space: 0,
        condition_id: null,
        objective_id: null,
        images: [],
      };
      imagesPreviews.value = [];
      tempImage.value = null;
      showImageError.value = false;
    };

    return {
      form,
      loading,
      loader,
      tempImage,
      showImageError,
      propertyTypes,
      conditions,
      objectives,
      currencies,
      imagesPreviews,
      onImageSelected,
      removeImage,
      onRejected,
      onSubmit,
      onReset,
    };
  },
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────────── */
.form-page {
  background: #f4f6fb;
  min-height: 100vh;
}

/* ── Page header ──────────────────────────────────────── */
.page-header {
  background: #0d1b2a;
  padding: 20px 24px;
}
.page-header-inner {
  display: flex;
  align-items: center;
  max-width: 860px;
  margin: 0 auto;
}
.back-btn {
  color: rgba(255, 255, 255, 0.7) !important;
  background: rgba(255, 255, 255, 0.08) !important;
}
.back-btn:hover {
  background: rgba(255, 255, 255, 0.14) !important;
}
.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}
.page-subtitle {
  font-size: 12px;
  color: rgba(212, 175, 55, 0.75);
  letter-spacing: 0.4px;
  margin-top: 2px;
}

/* ── Form wrapper ─────────────────────────────────────── */
.form-wrapper {
  max-width: 860px;
  margin: 32px auto;
  padding: 0 16px 48px;
}
.form-card {
  border-radius: 16px;
  border: 1px solid #e8ecf4;
  box-shadow: 0 4px 24px rgba(13, 27, 42, 0.07);
  overflow: hidden;
  background: #ffffff;
}

/* ── Section ──────────────────────────────────────────── */
.form-section {
  padding: 28px 32px;
}
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}
.section-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #0d1b2a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #0d1b2a;
  margin-left: 12px;
  line-height: 1.2;
}
.section-sub {
  font-size: 12px;
  color: #94a3b8;
  margin-left: 12px;
  margin-top: 2px;
}
.form-divider {
  height: 1px;
  background: #f0f4fa;
  margin: 0 32px;
}
.form-field {
  margin-bottom: 4px;
}

/* ── Upload zone ──────────────────────────────────────── */
.upload-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  transition: border-color 0.2s, background 0.2s;
  cursor: pointer;
  background: #f8fafc;
}
.upload-zone:hover:not(.upload-zone--disabled) {
  border-color: #d4af37;
  background: rgba(212, 175, 55, 0.04);
}
.upload-zone--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.upload-input {
  width: 100%;
}
.upload-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  pointer-events: none;
}
.upload-icon {
  color: #d4af37;
  margin-bottom: 8px;
}
.upload-text {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
}
.upload-hint {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
}

/* ── Image previews ───────────────────────────────────── */
.preview-card {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}
.preview-img {
  border-radius: 10px;
}
.preview-overlay {
  position: absolute;
  top: 6px;
  right: 6px;
  opacity: 0;
  transition: opacity 0.2s;
}
.preview-card:hover .preview-overlay {
  opacity: 1;
}
.preview-label {
  font-size: 10px;
  color: #64748b;
  text-align: center;
  padding: 4px 6px;
  background: #f8fafc;
}

/* ── Actions ──────────────────────────────────────────── */
.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 20px 32px 28px;
  border-top: 1px solid #f0f4fa;
  background: #fafbfd;
}
.action-btn {
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  padding: 8px 20px;
  letter-spacing: 0.3px;
}
.action-btn--cancel {
  color: #94a3b8 !important;
}
.action-btn--cancel:hover {
  background: #f1f5f9 !important;
  color: #64748b !important;
}
.action-btn--reset {
  color: #64748b !important;
}
.action-btn--reset:hover {
  background: #f1f5f9 !important;
}
.action-btn--submit {
  background: #0d1b2a !important;
  color: #d4af37 !important;
  padding: 10px 28px;
}
.action-btn--submit:hover {
  background: #162333 !important;
}
</style>
